import { cancelFunnelChannelSessionReads } from 'src/modules/shared/communications/utils/cache/funnel-channel-sessions/update.util';
import {
  acceptRealtimeResourceVersion,
  isAmbiguousResourceVersion,
} from './event-order.util';
import { recoverCanonicalConversation } from 'src/modules/shared/communications/services/canonical-conversation-recovery.service';
import type { QueryClient } from '@tanstack/react-query';
import { applyCallRealtimePayload } from './call-realtime.util';
import { cancelPendingCallReads } from './call-read-cancellation.util';
import { applyChannelSessionRealtimePayload } from './channel-session-realtime.util';
import { applyConversationRealtimePayload } from './conversation-realtime.util';
import { applyMessageRealtimePayload } from './message-realtime.util';
import { applyStatusHistoryRealtimePayload } from './status-history-realtime.util';
import type { TCRealtimePayload } from '../../types/realtime-payload.types';

/**
 * Applies a validated Communications realtime payload to matching React Query caches.
 *
 * @param queryClient - React Query client that owns the Communications caches.
 * @param payload - Validated realtime payload emitted by socket-connection.
 * @returns Whether the existing coalesced HTTP reads must settle an ambiguous resource.
 */
export const applyCommunicationsRealtimePayload = (
  queryClient: QueryClient,
  payload: TCRealtimePayload
): boolean => {
  const ambiguous = isAmbiguousResourceVersion(queryClient, payload);
  if (!acceptRealtimeResourceVersion(queryClient, payload)) return false;
  if (ambiguous) {
    if (payload.resourceType === 'ChannelSession') {
      cancelFunnelChannelSessionReads({
        queryClient,
        action: payload.action,
        resourceId: payload.resourceId,
        funnelConversationId: payload.funnelConversationId,
        channelSession: payload.resource,
      });
      return true;
    }
    if (payload.resourceType === 'Call') {
      cancelPendingCallReads(queryClient, payload);
      return true;
    }
    void recoverCanonicalConversation(
      queryClient,
      payload.funnelConversationId,
      payload.tenantId,
      payload.occurredAt
    );
    return false;
  }

  switch (payload.resourceType) {
    case 'Message':
      applyMessageRealtimePayload(queryClient, payload);
      return false;
    case 'Call': {
      const interrupted = cancelPendingCallReads(queryClient, payload);
      applyCallRealtimePayload(queryClient, payload);
      return interrupted;
    }
    case 'StatusHistory':
      applyStatusHistoryRealtimePayload(queryClient, payload);
      return false;
    case 'Conversation':
      applyConversationRealtimePayload(queryClient, payload);
      return false;
    case 'ChannelSession':
      applyChannelSessionRealtimePayload(queryClient, payload);
      return false;
  }
};
